import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { appEnv } from '@/app/env/appEnv';
import { startGateScreenPolling, useRemoteConfigStore } from '@/app/remote_config/handler/remoteConfigStore';
import { minimumVersionForWeb } from '@/app/remote_config/domain/remoteAppConfig';
import { PixelContainer } from '@/ui/pixel/PixelContainer';
import { useThemeColors } from '@/settings/handler/settingsStore';

export function ForceUpdateScreen() {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const refresh = useRemoteConfigStore((s) => s.refresh);
  const required = useRemoteConfigStore((s) =>
    s.config ? minimumVersionForWeb(s.config) : '—',
  );
  const [checking, setChecking] = useState(false);

  const onCheckAgain = async () => {
    if (checking) return;
    setChecking(true);
    try {
      await refresh();
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => startGateScreenPolling(), []);

  return (
    <div className="flex h-full items-center justify-center px-7" style={{ backgroundColor: colors.surface }}>
      <div className="flex w-full max-w-[420px] flex-col items-center">
        <h1 className="leading-none" style={{ fontSize: 40, color: colors.onSurface }}>
          {t('appName')}
        </h1>
        <div className="mt-7 w-full">
          <PixelContainer
            radius={3}
            pixelScale={2}
            padding="20px"
            backgroundColor={colors.surfaceContainerHighest}
            borderColor={colors.onSurface}
          >
            <h2 className="text-center leading-tight" style={{ fontSize: 28, color: colors.onSurface }}>
              {t('forceUpdateTitle')}
            </h2>
            <p
              className="mt-3 text-center leading-snug"
              style={{ fontSize: 18, color: colors.onSurface, opacity: 0.75 }}
            >
              {t('forceUpdateMessage')}
            </p>
            <div className="mt-5">
              <VersionRow label={t('forceUpdateCurrent')} version={appEnv.version} />
              <div className="h-2.5" />
              <VersionRow label={t('forceUpdateRequired')} version={required} />
            </div>
          </PixelContainer>
        </div>
        <div className="mt-6 w-full">
          <PixelContainer
            radius={9}
            pixelScale={2}
            padding="16px 0"
            backgroundColor={colors.onSurface}
            borderColor={colors.onSurface}
            onClick={() => window.location.reload()}
          >
            <span className="block text-center leading-none" style={{ fontSize: 22, color: colors.surface }}>
              {t('forceUpdateCta')}
            </span>
          </PixelContainer>
        </div>
        <div className="mt-3 w-full">
          <PixelContainer
            radius={9}
            pixelScale={2}
            padding="14px 28px"
            backgroundColor={colors.surfaceContainerHighest}
            borderColor={colors.onSurface}
            onClick={checking ? undefined : () => void onCheckAgain()}
          >
            <span className="block text-center leading-none" style={{ fontSize: 22, color: colors.onSurface }}>
              {checking ? t('offlineChecking') : t('offlineRetry')}
            </span>
          </PixelContainer>
        </div>
      </div>
    </div>
  );
}

function VersionRow({ label, version }: { label: string; version: string }) {
  const colors = useThemeColors();
  return (
    <div className="flex items-center">
      <span className="flex-1 leading-none" style={{ fontSize: 18, color: colors.onSurface, opacity: 0.65 }}>
        {label}
      </span>
      <span className="leading-none" style={{ fontSize: 22, color: colors.onSurface }}>
        {version}
      </span>
    </div>
  );
}
